// @flow

import * as React from "react";
import { useSelector } from "react-redux";

import formatCurrency from "../../utils/formatCurrency";
import Icon from "../atoms/Icon";
import CartItem from "./CartItem";
import AnimateHeight from "../atoms/AnimateHeight";

type PropsType = {
    className?: string,
    defaultCollapsed?: boolean,
};

const CheckoutSummary = (props: PropsType): React.Node => {
    const [collapsed, setCollapsed] = React.useState(props.defaultCollapsed);
    const cart = useSelector((state: any): any => state.cart);
    const subTotal = cart.totals
        ? Number(cart.totals.total_items) + Number(cart.totals.total_items_tax)
        : null;

    const cssBaseClass = "checkout-summary";
    const classList = [cssBaseClass];
    if (props.className) {
        classList.push(props.className);
    }

    if (collapsed) {
        classList.push("checkout-summary--collapsed");
    }

    return (
        <div className={classList.join(" ")}>
            <div
                className="checkout-summary__header"
                onClick={() => setCollapsed(!collapsed)}
            >
                <div className="checkout-summary__header__title">
                    Your order{" "}
                    <span className="checkout-summary__header__items">
                        <span className="checkout-summary__header__items__divider">
                            -
                        </span>{" "}
                        {cart.items_count}{" "}
                        {`Item${cart.items_count !== 1 ? "s" : ""}`}
                    </span>
                </div>
                <div className="checkout-summary__header__value">
                    <div className="checkout-summary__header__value__total">
                        {cart.totals
                            ? formatCurrency(cart.totals.total_price)
                            : null}
                    </div>
                    <Icon name="chevron-down" />
                </div>
            </div>

            <AnimateHeight
                className="checkout-summary__body"
                duration={500}
                hideOverflow
                height={!collapsed ? null : 0}
            >
                <div>
                    <div className="checkout-summary__items">
                        {cart.items
                            ? cart.items.map((cartItem: any, index: number) => {
                                  return (
                                      <CartItem
                                          {...cartItem}
                                          cartKey={cartItem.key}
                                          summary={true}
                                      />
                                  );
                              })
                            : null}
                    </div>

                    <div className="checkout-summary__lines">
                        {/* Subtotal */}
                        <div className="checkout-summary__line">
                            <div className="checkout-summary__line__title">
                                Subtotal (GST incl.)
                            </div>
                            <div className="checkout-summary__line__value">
                                {formatCurrency(subTotal)}
                            </div>
                        </div>

                        {/* Delivery */}
                        {cart.needs_shipping &&
                        Number(cart.totals.total_shipping) ? (
                            <div className="checkout-summary__line">
                                <div className="checkout-summary__line__title">
                                    Delivery
                                </div>
                                <div className="checkout-summary__line__value">
                                    {cart.totals
                                        ? formatCurrency(
                                              Number(
                                                  cart.totals.total_shipping
                                              ) +
                                                  Number(
                                                      cart.totals
                                                          .total_shipping_tax
                                                  )
                                          )
                                        : null}
                                </div>
                            </div>
                        ) : null}

                        {/* Applied promo/coupons */}
                        {cart.coupons
                            ? cart.coupons.map(
                                  (promoCode: any, index: number) => {
                                      return (
                                          <div
                                              key={index}
                                              className="checkout-summary__line"
                                          >
                                              <div className="checkout-summary__line__title">
                                                  Promo code -{" "}
                                                  <strong>
                                                      {promoCode.code}
                                                  </strong>
                                              </div>
                                              {promoCode.totals ? (
                                                  <div className="checkout-summary__line__value">
                                                      -
                                                      {formatCurrency(
                                                          Number(
                                                              promoCode.totals
                                                                  .total_discount
                                                          ) +
                                                              Number(
                                                                  promoCode
                                                                      .totals
                                                                      .total_discount_tax
                                                              )
                                                      )}
                                                  </div>
                                              ) : null}
                                          </div>
                                      );
                                  }
                              )
                            : null}
                    </div>

                    {/* Total */}
                    <div className="checkout-summary__footer">
                        <div className="checkout-summary__footer__title">
                            Total
                        </div>
                        <div className="checkout-summary__footer__value">
                            {cart.totals
                                ? formatCurrency(cart.totals.total_price)
                                : null}
                        </div>
                    </div>
                </div>
            </AnimateHeight>
        </div>
    );
};

export default CheckoutSummary;
